import { Box, Button, Typography, Container, Fade, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MarkEmailRead, ErrorOutline, Schedule } from "@mui/icons-material";
import {
  ForgotPasswordMainContainer,
  ForgotPasswordCard,
  ForgotPasswordHeader,
} from "../style";
import {
  useConfirmEmailVerificationMutation,
  useRequestEmailVerificationMutation,
} from "../../../store/api/usersApiSlice";
import {
  isEmailVerificationTokenExpired,
  isEmailVerificationTokenInvalid,
} from "../../../utils/emailVerificationErrors";

const STATUS = {
  READY: "ready",
  MISSING_PARAMS: "missing_params",
  SUCCESS: "success",
  INVALID: "invalid",
  EXPIRED: "expired",
};

const VerifyEmail = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmEmailVerification] = useConfirmEmailVerificationMutation();
  const [requestEmailVerification] = useRequestEmailVerificationMutation();
  const token = searchParams.get("token");
  const email = searchParams.get("email") || "";
  const hasParams = Boolean(token && email);

  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [status, setStatus] = useState(
    hasParams ? STATUS.READY : STATUS.MISSING_PARAMS
  );

  useEffect(() => {
    if (!hasParams) {
      setStatus(STATUS.MISSING_PARAMS);
      return;
    }
    setStatus((prev) =>
      prev === STATUS.MISSING_PARAMS ? STATUS.READY : prev
    );
  }, [hasParams]);

  const continueUrl = useMemo(
    () => `${window.location.origin}/verify-email`,
    []
  );

  const handleConfirm = async () => {
    if (!token || !email) {
      setStatus(STATUS.MISSING_PARAMS);
      return;
    }
    try {
      setSubmitting(true);
      await confirmEmailVerification({ email, token }).unwrap();
      setStatus(STATUS.SUCCESS);
    } catch (err) {
      if (isEmailVerificationTokenExpired(err)) {
        setStatus(STATUS.EXPIRED);
      } else if (isEmailVerificationTokenInvalid(err)) {
        setStatus(STATUS.INVALID);
      } else {
        toast.error(t("something_went_wrong"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      setResending(true);
      await requestEmailVerification({ email, continueUrl }).unwrap();
      toast.success(t("email_verification_resent"));
    } catch {
      toast.error(t("something_went_wrong"));
    } finally {
      setResending(false);
    }
  };

  const primaryButtonSx = {
    textTransform: "none",
    borderRadius: "12px",
    height: "48px",
    fontWeight: 600,
  };

  const renderBody = () => {
    switch (status) {
      case STATUS.SUCCESS:
        return (
          <Box sx={{ textAlign: "center" }}>
            <MarkEmailRead
              sx={{ fontSize: 56, color: theme.palette.success.main, mb: 2 }}
            />
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {t("email_verification_success")}
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, mt: 1.5 }}>
              {t("email_verification_close_page")}
            </Typography>
          </Box>
        );

      case STATUS.INVALID:
        return (
          <Box sx={{ textAlign: "center" }}>
            <ErrorOutline
              sx={{ fontSize: 56, color: theme.palette.error.main, mb: 2 }}
            />
            <Typography sx={{ color: theme.palette.text.secondary, mb: 3 }}>
              {t("email_verification_invalid_link")}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/login", { replace: true })}
              sx={primaryButtonSx}
            >
              {t("go_to_login")}
            </Button>
          </Box>
        );

      case STATUS.EXPIRED:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Schedule
              sx={{ fontSize: 56, color: theme.palette.warning.main, mb: 2 }}
            />
            <Typography sx={{ color: theme.palette.text.secondary, mb: 3 }}>
              {t("email_verification_expired_link")}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              disabled={resending || !email}
              onClick={handleResend}
              sx={{ ...primaryButtonSx, mb: 1.5 }}
            >
              {resending ? t("sending") : t("email_verification_resend")}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate("/login", { replace: true })}
              sx={{ textTransform: "none" }}
            >
              {t("go_to_login")}
            </Button>
          </Box>
        );

      case STATUS.MISSING_PARAMS:
        return (
          <Box sx={{ textAlign: "center" }}>
            <ErrorOutline
              sx={{ fontSize: 56, color: theme.palette.error.main, mb: 2 }}
            />
            <Typography sx={{ color: theme.palette.text.secondary, mb: 3 }}>
              {t("email_verification_missing_params")}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/login", { replace: true })}
              sx={primaryButtonSx}
            >
              {t("go_to_login")}
            </Button>
          </Box>
        );

      case STATUS.READY:
      default:
        return (
          <Box sx={{ textAlign: "center" }}>
            {email && (
              <Typography sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                {email}
              </Typography>
            )}
            <Typography sx={{ color: theme.palette.text.secondary, mb: 3 }}>
              {t("email_verification_ready_text")}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              disabled={submitting}
              onClick={handleConfirm}
              sx={primaryButtonSx}
            >
              {submitting ? t("sending") : t("email_verification_confirm")}
            </Button>
          </Box>
        );
    }
  };

  return (
    <ForgotPasswordMainContainer>
      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <ForgotPasswordCard>
            <ForgotPasswordHeader>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  textAlign: "center",
                  flex: 1,
                }}
              >
                {t("email_verification_title")}
              </Typography>
            </ForgotPasswordHeader>
            {renderBody()}
          </ForgotPasswordCard>
        </Fade>
      </Container>
    </ForgotPasswordMainContainer>
  );
};

export default VerifyEmail;
